import { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const HEADING_STYLES = [
  { tag: "h1", label: "Título 1" },
  { tag: "h2", label: "Título 2" },
  { tag: "h3", label: "Título 3" },
  { tag: "p", label: "Párrafo" },
];

const ALIGNMENT_OPTIONS = [
  {
    value: "justifyLeft",
    align: "left",
    label: "Izquierda",
    icon: <FormatAlignLeftIcon fontSize="small" />,
  },
  {
    value: "justifyCenter",
    align: "center",
    label: "Centro",
    icon: <FormatAlignCenterIcon fontSize="small" />,
  },
  {
    value: "justifyRight",
    align: "right",
    label: "Derecha",
    icon: <FormatAlignRightIcon fontSize="small" />,
  },
];

const AUTO_LINK_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)(\s)$/i;
const AUTO_LINK_AT_CURSOR_PATTERN = /((?:https?:\/\/|www\.)[^\s<]+|(?:[a-z0-9-]+\.)+[a-z]{2,}(?:\/[^\s<]*)?)$/i;
const EDITOR_BLOCK_SELECTOR = "h1, h2, h3, p, div, li";

function normalizeLinkUrl(url) {
  const trimmed = String(url || "").trim();
  if (/^(https?:\/\/|mailto:)/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function getDisplayUrl(url) {
  try {
    const parsed = new URL(normalizeLinkUrl(url));
    return `${parsed.hostname}${parsed.pathname === "/" ? "" : parsed.pathname}`;
  } catch {
    return url;
  }
}

function normalizeNbsp(value) {
  return String(value || "").replace(/&(?:amp;)?nbsp;|\u00a0/gi, " ");
}

function normalizeListLine(value) {
  return normalizeNbsp(value).replace(/\s+/g, " ").trim();
}

function escapeHtml(text) {
  return String(text || "").replace(/[&<>"']/g, (char) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;",
    };

    return entities[char] || char;
  });
}

function getSelectionContainer(range) {
  if (!range) return null;

  return range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
    ? range.commonAncestorContainer
    : range.commonAncestorContainer.parentNode;
}

function buildListHtml(command, lines) {
  const listTag = command === "insertOrderedList" ? "ol" : "ul";

  const itemsHtml = lines
    .map((line) => `<li>${escapeHtml(line)}</li>`)
    .join("");

  return `<${listTag}>${itemsHtml}</${listTag}>`;
}

function getBlocksInsideRange(editor, range) {
  if (!editor || !range) return [];

  const blocks = Array.from(editor.querySelectorAll(EDITOR_BLOCK_SELECTOR)).filter((block) => {
    try {
      return range.intersectsNode(block);
    } catch {
      return false;
    }
  });

  if (blocks.length) return blocks;

  const container = getSelectionContainer(range);
  const closestBlock = container?.closest?.(EDITOR_BLOCK_SELECTOR);

  return closestBlock && editor.contains(closestBlock) ? [closestBlock] : [];
}

function isEditorContentEmpty(value) {
  const normalized = normalizeNbsp(value)
    .replace(/<br\s*\/?>/gi, "")
    .replace(/<\/?(p|div)>/gi, "")
    .trim();

  return !normalized;
}

const TOOLBAR_BUTTON_SX = {
  width: 32,
  height: 32,
  borderRadius: "6px",
  border: "1px solid transparent",
  bgcolor: "transparent",
  color: "neutral.700",
  "&:hover": {
    bgcolor: "neutral.100",
    borderColor: "neutral.200",
  },
};

const toolbarButtonSx = (active = false) => ({
  ...TOOLBAR_BUTTON_SX,
  bgcolor: active ? "secondary.100" : "transparent",
  color: active ? "primary.900" : "neutral.700",
  borderColor: active ? "secondary.300" : "transparent",
});

const TOOLBAR_DISABLED_BUTTON_SX = {
  ...TOOLBAR_BUTTON_SX,
  bgcolor: "neutral.50 !important",
  color: "neutral.400",
  borderColor: "neutral.200",
};

const TOOLBAR_DIVIDER_SX = {
  borderColor: "neutral.200",
  mx: 0.5,
};

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = 300,
  readOnly = false,
  blockStyles = HEADING_STYLES,
}) {
  const editorRef = useRef(null);
  const lastSelectionRef = useRef(null);
  const editingLinkRef = useRef(null);

  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [alignment, setAlignment] = useState("justifyLeft");
  const [currentStyle, setCurrentStyle] = useState("p");
  const [savedSelection, setSavedSelection] = useState(null);
  const [hasTextSelection, setHasTextSelection] = useState(false);
  const [linkPopover, setLinkPopover] = useState({
    anchorEl: null,
    href: "",
    text: "",
  });
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
  });
  const availableBlockStyles = blockStyles?.length ? blockStyles : HEADING_STYLES;
  const selectedBlockStyle = availableBlockStyles.some((style) => style.tag === currentStyle)
    ? currentStyle
    : availableBlockStyles[0].tag;

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const normalizedValue = normalizeNbsp(value);
    const normalizedCurrentHtml = normalizeNbsp(editor.innerHTML);

    if (normalizedCurrentHtml === normalizedValue) return;

    const activeElement = document.activeElement;
    const editorHasFocus = activeElement === editor || editor.contains(activeElement);

    if (!editorHasFocus) {
      editor.innerHTML = normalizedValue;
    }
  }, [value]);

  const restoreLastSelection = () => {
    if (!lastSelectionRef.current) return;

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(lastSelectionRef.current);
  };

  const resetToolbarState = () => {
    setActiveStyles({
      bold: false,
      italic: false,
      underline: false,
      strikethrough: false,
    });
    setHasTextSelection(false);
    setAlignment("justifyLeft");
    setCurrentStyle("p");
  };

  const updateActiveStyles = () => {
    if (typeof document === "undefined") return;

    const editor = editorRef.current;
    const selection = window.getSelection();

    if (!editor || !selection || selection.rangeCount === 0) {
      resetToolbarState();
      return;
    }

    const range = selection.getRangeAt(0);
    let parent = range.startContainer;

    if (parent.nodeType === Node.TEXT_NODE) parent = parent.parentNode;

    const selectionInsideEditor = editor.contains(parent);

    if (!selectionInsideEditor) {
      resetToolbarState();
      return;
    }

    setActiveStyles({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikethrough: document.queryCommandState("strikeThrough"),
    });

    const nextSelectedText = selection.toString().trim();
    const hasSelection = Boolean(nextSelectedText);

    setHasTextSelection(hasSelection);
    lastSelectionRef.current = range.cloneRange();

    if (hasSelection) {
      setSelectedText(nextSelectedText);
      setSavedSelection(range.cloneRange());
    }

    const alignNode = parent.closest?.(EDITOR_BLOCK_SELECTOR);
    const textAlign = alignNode?.style?.textAlign;
    const alignOption = ALIGNMENT_OPTIONS.find((option) => option.align === textAlign);

    if (alignOption) {
      setAlignment(alignOption.value);
    } else if (document.queryCommandState("justifyCenter")) {
      setAlignment("justifyCenter");
    } else if (document.queryCommandState("justifyRight")) {
      setAlignment("justifyRight");
    } else {
      setAlignment("justifyLeft");
    }

    const closestHeading = parent.closest?.("h1, h2, h3, p");

    if (closestHeading) {
      setCurrentStyle(closestHeading.tagName.toLowerCase());
    } else {
      setCurrentStyle("p");
    }
  };

  const handleEditorChange = () => {
    if (editorRef.current && !readOnly) {
      onChange?.(normalizeNbsp(editorRef.current.innerHTML));
      updateActiveStyles();
    }
  };

  const execCommand = (command, commandValue = null) => {
    if (readOnly) return;

    editorRef.current?.focus();
    restoreLastSelection();
    document.execCommand(command, false, commandValue);
    editorRef.current?.focus();
    handleEditorChange();
  };

  const closeUrlDialog = () => {
    editingLinkRef.current = null;
    setUrlDialogOpen(false);
    setUrlInput("");
    setSelectedText("");
    setSavedSelection(null);
  };

  const handlePaste = (event) => {
    if (readOnly) return;

    event.preventDefault();

    const text = event.clipboardData.getData("text/plain");
    if (!text) return;

    const normalizedText = normalizeNbsp(text);

    const blocks = normalizedText
      .split(/\n{2,}/)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean);

    const html = blocks.length
      ? blocks
          .map((paragraph) => {
            const lines = paragraph
              .split(/\r?\n/)
              .map((line) => escapeHtml(line.trim()))
              .filter(Boolean);

            if (lines.length <= 1) {
              return `<p>${lines[0] || "<br>"}</p>`;
            }

            return `<p>${lines.join("<br>")}</p>`;
          })
          .join("")
      : `<p>${escapeHtml(normalizedText)}</p>`;

    document.execCommand("insertHTML", false, html);
    handleEditorChange();
  };

  const handleHeadingClick = (tag) => {
    execCommand("formatBlock", tag === "p" ? "<p>" : `<${tag}>`);
  };

  const handleLinkClick = () => {
    if (!hasTextSelection || !savedSelection || !selectedText.trim()) return;

    editingLinkRef.current = null;
    setUrlInput("");
    setUrlDialogOpen(true);
  };

  const handleAddLink = () => {
    if (!urlInput.trim()) {
      alert("Por favor, ingresa una URL válida.");
      return;
    }

    const url = normalizeLinkUrl(urlInput);

    if (editingLinkRef.current) {
      const link = editingLinkRef.current;

      link.setAttribute("href", url);
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");

      editingLinkRef.current = null;
      handleEditorChange();
      closeUrlDialog();
      return;
    }

    if (savedSelection && editorRef.current) {
      editorRef.current.focus();

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);

      document.execCommand("createLink", false, url);

      const links = editorRef.current.querySelectorAll("a");
      links.forEach((link) => {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      });

      handleEditorChange();
    }

    closeUrlDialog();
  };

  const applyAlignment = (command, alignValue) => {
    if (readOnly) return;

    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreLastSelection();

    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    const range = selection.getRangeAt(0);
    const container = getSelectionContainer(range);

    if (!container || !editor.contains(container)) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    const blocks = getBlocksInsideRange(editor, range);

    if (!blocks.length) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    blocks.forEach((block) => {
      block.style.textAlign = alignValue;
    });

    setAlignment(command);
    editor.focus();
    handleEditorChange();
  };

  const applyList = (command) => {
    if (readOnly) return;

    const editor = editorRef.current;
    if (!editor) return;

    editor.focus();
    restoreLastSelection();

    const selection = window.getSelection();

    if (!selection || selection.rangeCount === 0) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    const range = selection.getRangeAt(0);
    const container = getSelectionContainer(range);

    if (!container || !editor.contains(container)) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    if (range.collapsed) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    const selectedText = selection.toString();

    const lines = selectedText
      .split(/\r?\n/)
      .map(normalizeListLine)
      .filter(Boolean);

    if (lines.length <= 1) {
      document.execCommand(command, false, null);
      handleEditorChange();
      return;
    }

    const listHtml = buildListHtml(command, lines);

    document.execCommand("insertHTML", false, listHtml);

    const links = editor.querySelectorAll("a");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });

    editor.focus();
    handleEditorChange();
  };

  const autoLinkLastUrl = ({ allowNoTrailingSpace = false } = {}) => {
    const editor = editorRef.current;
    const selection = window.getSelection();

    if (!editor || !selection?.rangeCount || !selection.isCollapsed) return;

    const range = selection.getRangeAt(0);
    const textNode = range.startContainer;

    if (textNode.nodeType !== Node.TEXT_NODE) return;
    if (!editor.contains(textNode)) return;

    const parent = textNode.parentNode;
    if (!parent || parent.closest?.("a")) return;

    const textBeforeCursor = textNode.textContent.slice(0, range.startOffset);
    const match = textBeforeCursor.match(
      allowNoTrailingSpace ? AUTO_LINK_AT_CURSOR_PATTERN : AUTO_LINK_PATTERN,
    );

    if (!match) return;

    const urlText = match[1];
    const trailingText = match[2] || "";
    const urlEnd = range.startOffset - trailingText.length;
    const urlStart = urlEnd - urlText.length;
    const before = textNode.textContent.slice(0, urlStart);
    const after = textNode.textContent.slice(urlEnd);

    const link = document.createElement("a");
    link.href = normalizeLinkUrl(urlText);
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = urlText;

    const afterNode = document.createTextNode(after);

    if (before) parent.insertBefore(document.createTextNode(before), textNode);
    parent.insertBefore(link, textNode);
    parent.insertBefore(afterNode, textNode);
    parent.removeChild(textNode);

    const nextRange = document.createRange();
    nextRange.setStart(afterNode, Math.min(trailingText.length, afterNode.length));
    nextRange.collapse(true);

    selection.removeAllRanges();
    selection.addRange(nextRange);
    lastSelectionRef.current = nextRange.cloneRange();
    handleEditorChange();
  };

  const handleEditorKeyUp = (event) => {
    if (event.key === " ") {
      autoLinkLastUrl();
    }

    updateActiveStyles();
  };

  const handleEditorKeyDown = (event) => {
    if (event.key === "Enter") {
      autoLinkLastUrl({ allowNoTrailingSpace: true });
    }
  };

  const handleEditorClick = (event) => {
    const link = event.target.closest?.("a");

    if (link && editorRef.current?.contains(link)) {
      event.preventDefault();
      setLinkPopover({
        anchorEl: link,
        href: link.getAttribute("href") || "",
        text: link.textContent || "",
      });
      return;
    }

    closeLinkPopover();
  };

  const closeLinkPopover = () => {
    setLinkPopover({ anchorEl: null, href: "", text: "" });
  };

  const openCurrentLink = () => {
    if (linkPopover.href) {
      window.open(linkPopover.href, "_blank", "noopener,noreferrer");
    }

    closeLinkPopover();
  };

  const copyCurrentLink = async () => {
    if (!linkPopover.href) return;

    try {
      await navigator.clipboard.writeText(linkPopover.href);
    } catch {
      // Clipboard access may be blocked by the browser; the link remains visible.
    }
  };

  const editCurrentLink = () => {
    const link = linkPopover.anchorEl;
    if (!link) return;

    editingLinkRef.current = link;
    setSelectedText(link.textContent || "");
    setUrlInput(link.getAttribute("href") || "");
    closeLinkPopover();
    setUrlDialogOpen(true);
  };

  const removeCurrentLink = () => {
    const link = linkPopover.anchorEl;
    if (!link) return;

    editorRef.current?.focus();

    const range = document.createRange();
    range.selectNodeContents(link);

    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);

    document.execCommand("unlink", false, null);
    closeLinkPopover();
    handleEditorChange();
  };

  return (
    <Stack spacing={1} sx={{ width: "100%" }}>
      <Box
        sx={{
          bgcolor: "white",
          border: "1px solid",
          borderColor: readOnly ? "neutral.200" : "neutral.300",
          borderRadius: "14px",
          overflow: "visible",
          position: "relative",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": !readOnly && {
            borderColor: "primary.500",
            boxShadow: "0 0 0 3px rgba(24, 34, 93, 0.08)",
          },
        }}
      >
        {!readOnly && (
          <Stack
            spacing={1}
            sx={{
              px: 1.5,
              py: 1,
              position: "sticky",
              top: 0,
              zIndex: 20,
              bgcolor: "white",
              borderBottom: "1px solid",
              borderColor: "neutral.200",
              borderTopLeftRadius: "13px",
              borderTopRightRadius: "13px",
              boxShadow: "0 1px 0 rgba(15, 23, 42, 0.04)",
            }}
          >
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", gap: 1 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={selectedBlockStyle}
                  displayEmpty
                  onChange={(event) => {
                    const tag = event.target.value;
                    setCurrentStyle(tag);
                    handleHeadingClick(tag);
                  }}
                  sx={{
                    height: 32,
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: 600,
                    bgcolor: "transparent",
                    "& .MuiSelect-select": { py: 0.5 },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "neutral.200" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.500" },
                  }}
                >
                  {availableBlockStyles.map((style) => (
                    <MenuItem
                      key={style.tag}
                      value={style.tag}
                      sx={{
                        fontWeight: style.tag.startsWith("h") ? 700 : 400,
                        fontSize:
                          style.tag === "h1"
                            ? "1.2rem"
                            : style.tag === "h2"
                              ? "1.1rem"
                              : style.tag === "h3"
                                ? "1rem"
                                : "0.9rem",
                      }}
                    >
                      {style.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Negrita (Ctrl+B)">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => execCommand("bold")}
                    sx={toolbarButtonSx(activeStyles.bold)}
                  >
                    <FormatBoldIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cursiva (Ctrl+I)">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => execCommand("italic")}
                    sx={toolbarButtonSx(activeStyles.italic)}
                  >
                    <FormatItalicIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Subrayado (Ctrl+U)">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => execCommand("underline")}
                    sx={toolbarButtonSx(activeStyles.underline)}
                  >
                    <FormatUnderlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Tachado">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => execCommand("strikeThrough")}
                    sx={toolbarButtonSx(activeStyles.strikethrough)}
                  >
                    <StrikethroughSIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider orientation="vertical" flexItem sx={TOOLBAR_DIVIDER_SX} />

              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Lista con viñetas">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyList("insertUnorderedList")}
                    sx={TOOLBAR_BUTTON_SX}
                  >
                    <FormatListBulletedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Lista numerada">
                  <IconButton
                    size="small"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => applyList("insertOrderedList")}
                    sx={TOOLBAR_BUTTON_SX}
                  >
                    <FormatListNumberedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Divider orientation="vertical" flexItem sx={TOOLBAR_DIVIDER_SX} />

              <Tooltip title="Alineación">
                <ToggleButtonGroup
                  value={alignment}
                  exclusive
                  onChange={(event, newAlignment) => {
                    if (newAlignment !== null) {
                      setAlignment(newAlignment);
                      const selectedOption = ALIGNMENT_OPTIONS.find(
                        (option) => option.value === newAlignment,
                      );
                      applyAlignment(newAlignment, selectedOption?.align || "left");
                    }
                  }}
                  size="small"
                  sx={{ height: 32 }}
                >
                  {ALIGNMENT_OPTIONS.map((option) => (
                    <ToggleButton
                      key={option.value}
                      value={option.value}
                      onMouseDown={(event) => event.preventDefault()}
                      sx={{
                        borderRadius: "6px !important",
                        mx: 0.25,
                        border: "1px solid transparent !important",
                        color: "neutral.700",
                        "&:hover": {
                          bgcolor: "neutral.100",
                          borderColor: "neutral.200 !important",
                        },
                        "&.Mui-selected": {
                          bgcolor: "secondary.100 !important",
                          color: "primary.900 !important",
                          borderColor: "secondary.300 !important",
                        },
                      }}
                    >
                      {option.icon}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={TOOLBAR_DIVIDER_SX} />

              <Stack direction="row" spacing={0.5}>
                <Tooltip title="Agregar enlace">
                  <IconButton
                    size="small"
                    disabled={!hasTextSelection}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={handleLinkClick}
                    sx={{
                      ...TOOLBAR_BUTTON_SX,
                      color: hasTextSelection ? "neutral.700" : "neutral.400",
                    }}
                  >
                    <LinkIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Próximamente">
                  <span>
                    <IconButton size="small" disabled sx={TOOLBAR_DISABLED_BUTTON_SX}>
                      <ImageIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Stack>
        )}

        <Box
          ref={editorRef}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          onClick={handleEditorClick}
          onInput={handleEditorChange}
          onBlur={handleEditorChange}
          onSelect={updateActiveStyles}
          onKeyDown={handleEditorKeyDown}
          onKeyUp={handleEditorKeyUp}
          onMouseUp={updateActiveStyles}
          onPaste={handlePaste}
          data-placeholder={placeholder}
          className={!isEditorContentEmpty(value) ? "has-content" : ""}
          sx={{
            flex: 1,
            px: 2.5,
            py: 2.25,
            minHeight: `${minHeight}px`,
            bgcolor: readOnly ? "neutral.50" : "white",
            borderBottomLeftRadius: "13px",
            borderBottomRightRadius: "13px",
            overflow: "auto",
            outline: "none",
            position: "relative",
            "&:not(.has-content)::before": {
              content: "attr(data-placeholder)",
              color: "neutral.400",
              position: "absolute",
              pointerEvents: "none",
              display: "block",
            },
            fontSize: "14px",
            lineHeight: "1.6",
            color: "primary.900",
            "& h1": { fontSize: "28px", fontWeight: 700, margin: "0.5em 0" },
            "& h2": { fontSize: "22px", fontWeight: 700, margin: "0.5em 0" },
            "& h3": { fontSize: "18px", fontWeight: 700, margin: "0.5em 0" },
            "& p": { margin: "0.5em 0" },
            "& ul, & ol": { marginLeft: "1.5em", marginBottom: "0.5em" },
            "& li": { marginBottom: "0.25em" },
            "& a": {
              color: "#0056b3",
              textDecoration: "underline",
              cursor: "pointer",
              fontWeight: 700,
              "&:hover": {
                color: "#004494",
                textDecoration: "underline",
              },
            },
          }}
        />
      </Box>

      <Popover
        open={Boolean(linkPopover.anchorEl)}
        anchorEl={linkPopover.anchorEl}
        onClose={closeLinkPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.75,
            px: 1.25,
            py: 1,
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(15, 23, 42, 0.18)",
            border: "1px solid",
            borderColor: "neutral.200",
            maxWidth: 360,
          },
        }}
      >
        <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              bgcolor: "white",
              display: "grid",
              placeItems: "center",
              color: "#4285F4",
              fontWeight: 800,
              fontSize: 15,
              border: "1px solid",
              borderColor: "neutral.200",
              flexShrink: 0,
            }}
          >
            G
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              noWrap
              component="button"
              type="button"
              onClick={openCurrentLink}
              sx={{
                p: 0,
                m: 0,
                border: 0,
                bgcolor: "transparent",
                color: "#1a73e8",
                cursor: "pointer",
                display: "block",
                font: "inherit",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1.35,
                maxWidth: "100%",
                overflow: "hidden",
                textAlign: "left",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              {linkPopover.text || getDisplayUrl(linkPopover.href)}
            </Typography>
            <Typography noWrap sx={{ color: "neutral.700", fontSize: 12, lineHeight: 1.35 }}>
              {getDisplayUrl(linkPopover.href)}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Abrir enlace">
              <IconButton size="small" onClick={openCurrentLink}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copiar enlace">
              <IconButton size="small" onClick={copyCurrentLink}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Editar enlace">
              <IconButton size="small" onClick={editCurrentLink}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Quitar enlace">
              <IconButton size="small" onClick={removeCurrentLink}>
                <LinkOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Popover>

      <Dialog
        open={urlDialogOpen}
        onClose={closeUrlDialog}
        maxWidth="xs"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle sx={{ textAlign: "center" }}>
          <Box sx={{ px: 2, py: 1, bgcolor: "secondary.200", display: "inline-block" }}>
            Agregar enlace
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {selectedText && (
              <Box sx={{ p: 1.5, bgcolor: "neutral.50" }}>
                <span style={{ fontSize: "12px", color: "#606071" }}>Texto seleccionado:</span>
                <Box sx={{ fontWeight: 600, color: "primary.900", mt: 0.5 }}>
                  {selectedText}
                </Box>
              </Box>
            )}
            <TextField
              label="URL"
              placeholder="https://ejemplo.com"
              value={urlInput}
              onChange={(event) => setUrlInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  handleAddLink();
                }
              }}
              fullWidth
              size="small"
              autoFocus
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                onClick={closeUrlDialog}
                sx={{ textTransform: "none", fontWeight: 600, borderRadius: "12px" }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddLink}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "12px",
                  bgcolor: "primary.500",
                  color: "white",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Agregar
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
}
